import { Box, Divider, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, styled } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import CommonFormField from "../../common/components/CommonFormField";
import CommonButton from "../../common/components/CommonButton";
import SaveIcon from '@mui/icons-material/Save';
import { BorderColor, Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { getIntakeForm, saveIntakeForm, updateIntakeForm } from "../../api/authApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { UserTypeId } from "../../common/const";
import moment from "moment";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const IntakeForm = () => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [intakeId, setIntakeId] = useState(null);
    const [formData, setFormData] = useState({
        academic: {
            student_name: '',
            schoolname: '',
            grade: '',
        }
    });
    const [errors, setErrors] = useState({});
    const [courses, setCourses] = useState([]);
    const [activities, setActivities] = useState([]);

    const validateField = (name, value) => {
        switch (name) {
            case 'schoolname':
                if (!value || value.trim() === '') return 'School Name is required';
                if (value.length < 2) return 'School Name must be at least 2 characters';
                if (value.length > 100) return 'School Name must be less than 100 characters';
                return '';

            case 'grade':
                // Check if empty
                if (!value && value !== 0) return 'Grade is required';

                // Convert to string for uniform handling
                const gradeStr = String(value).trim();
                if (gradeStr === '') return 'Grade is required';

                // Check for valid grade formats

                // Option 1: Numeric grades (0-100)
                const numericGrade = Number(gradeStr);
                if (!isNaN(numericGrade)) {
                    if (numericGrade < 0) return 'Grade cannot be negative';
                    if (numericGrade > 100) return 'Grade cannot exceed 100';
                    if (numericGrade < 60) return 'Grade is below passing threshold';
                    return '';
                }

                // Option 2: Letter grades (A, B, C, D, F with +/-)
                const letterGradePattern = /^[A-F][+-]?$/i;
                if (letterGradePattern.test(gradeStr)) {
                    const upperGrade = gradeStr.toUpperCase();
                    if (upperGrade === 'F') return 'Grade F is failing';
                    return '';
                }

                // Option 3: Grade levels (Kindergarten, 1st, 2nd, etc.)
                const gradeLevelPattern = /^(K|Kindergarten|[1-9]|1[0-2])(st|nd|rd|th)?$/i;
                if (gradeLevelPattern.test(gradeStr)) {
                    return '';
                }

                // Option 4: GPA format (0.0 - 4.0)
                const gpaPattern = /^[0-4](\.\d{1,2})?$/;
                if (gpaPattern.test(gradeStr)) {
                    const gpa = parseFloat(gradeStr);
                    if (gpa < 2.0) return 'GPA is below minimum requirement';
                    return '';
                }

                return 'Please enter a valid grade (e.g., 85, A, B+, 3.5, 10th)';

            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            academic: {
                ...prev.academic,
                [name]: value,
            },
        }));

        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData.academic).forEach(key => {
            const error = validateField(key, formData.academic[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleSubmit = async () => {
        try {
            if (intakeId) {
                if (!validateForm()) return;
                dispatch(setLoading({
                    loading: true
                }));
                const response = await updateIntakeForm(intakeId, {
                    "student_name": formData?.academic?.student_name,
                    "school": formData.academic.schoolname,
                    "grade": formData.academic.grade,
                    "courses": courses,
                    "extracurriculars": activities
                });
                if (response.success) {
                    showNotification(response.message, 'success');
                    dispatch(setLoading({
                        loading: false
                    }));
                    getInitialData();
                }
            } else {
                if (!validateForm()) return;
                dispatch(setLoading({
                    loading: true
                }));
                const response = await saveIntakeForm({
                    "student_name": `${user?.first_name} ${user?.last_name}`,
                    "email": user?.email,
                    "school": formData.academic.schoolname,
                    "grade": formData.academic.grade,
                    "courses": courses.length > 0 ? courses : null,
                    "extracurriculars": activities?.length > 0 ? activities : null,
                    student_id: user?.student_id
                });
                if (response.success) {
                    showNotification(response.message, 'success');
                    dispatch(setLoading({
                        loading: false
                    }));
                    getInitialData();
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    // Course handlers
    const handleAddCourse = () => {
        const newId = courses.length > 0 ? Math.max(...courses.map(c => c.id)) + 1 : 1;
        setCourses([...courses, {
            id: newId,
            grade: '',
            sem1: '',
            fullYearGrade1: '',
            sem2: '',
            fullYearGrade2: '',
            totalSem: '',
            counselor: '',
            isEditing: true
        }]);
    };

    const handleEditCourse = (id) => {
        setCourses(courses.map(course =>
            course.id === id ? { ...course, isEditing: true } : course
        ));
    };

    const handleSaveCourse = (id) => {
        setCourses(courses.map(course =>
            course.id === id ? { ...course, isEditing: false } : course
        ));
    };

    const handleCancelCourse = (id) => {
        setCourses(courses.map(course =>
            course.id === id ? { ...course, isEditing: false } : course
        ));
    };

    const handleDeleteCourse = (id) => {
        setCourses(courses.filter(course => course.id !== id));
    };

    const handleCourseChange = (id, field, value) => {
        setCourses(courses.map(course =>
            course.id === id ? { ...course, [field]: value } : course
        ));
    };

    // Activity handlers
    const handleAddActivity = () => {
        const newId = activities.length > 0 ? Math.max(...activities.map(a => a.id)) + 1 : 1;
        setActivities([...activities, {
            id: newId,
            activity: '',
            date: '',
            location: '',
            volunteers: '',
            isEditing: true
        }]);
    };

    const handleEditActivity = (id) => {
        setActivities(activities.map(activity =>
            activity.id === id ? { ...activity, isEditing: true } : activity
        ));
    };

    const handleSaveActivity = (id) => {
        setActivities(activities.map(activity =>
            activity.id === id ? { ...activity, isEditing: false } : activity
        ));
    };

    const handleCancelActivity = (id) => {
        setActivities(activities.map(activity =>
            activity.id === id ? { ...activity, isEditing: false } : activity
        ));
    };

    const handleDeleteActivity = (id) => {
        setActivities(activities.filter(activity => activity.id !== id));
    };

    const handleActivityChange = (id, field, value) => {
        setActivities(activities.map(activity =>
            activity.id === id ? { ...activity, [field]: value } : activity
        ));
    };

    const getInitialData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getIntakeForm(user?.parent_email || user?.email);
            if (response?.data?.length > 0) {
                if (response?.data?.[0]?.student_intake) {
                    const { intake_id, school, grade, courses, extracurriculars, student_name } = response?.data?.[0]?.student_intake
                    setIntakeId(intake_id);
                    setFormData((prev) => ({
                        ...prev,
                        academic: {
                            ...prev.academic,
                            schoolname: school,
                            grade: grade,
                            student_name: student_name
                        },
                    }));
                    setActivities(extracurriculars || []);
                    setCourses(courses || []);
                }
            }
            dispatch(setLoading({
                loading: false
            }))
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.email) {
            getInitialData();
        }
    }, [user]);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonCard
                borderRadius={1}
                border={`1px solid ${theme.palette.background.secondary}`}
                paddingBottom="0px"
                padding="0px"
                marginTop="0px"
                headerContent={
                    <Box>
                        <Box
                            p={2}
                        >
                            <Box>
                                <Typography fontSize={22} fontWeight={600}>My Student Intake Form</Typography>
                                <Typography fontSize={15} color={theme.palette.background.dark}>Please fill out the student information and share it with us</Typography>
                            </Box>
                        </Box>
                        <Divider />
                    </Box>
                }
                content={
                    <Fragment>
                        <Grid container spacing={1} sx={{ padding: '13px' }}>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="School Name"
                                    type="email"
                                    name="schoolname"
                                    value={formData.academic.schoolname}
                                    onChange={handleInputChange}
                                    error={errors.schoolname}
                                    placeholder="Enter School Name"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Grade"
                                    type="text"
                                    name="grade"
                                    value={formData.academic.grade}
                                    onChange={handleInputChange}
                                    error={errors.grade}
                                    placeholder="Enter your grade"
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ padding: '13px' }}>
                            <Typography fontWeight={600} mb={1}>
                                Course Summary (Optional)
                            </Typography>
                            <TableContainer sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 1 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                            <Cell width="13%">Grade</Cell>
                                            <Cell width="13%">Sem 1</Cell>
                                            <Cell width="13%">Full Year Grade</Cell>
                                            <Cell width="13%">Sem 2</Cell>
                                            <Cell width="13%">Full Year Grade</Cell>
                                            <Cell width="13%">Total Sem</Cell>
                                            <Cell width="13%">Total\Semesters of H/AP/CL</Cell>
                                            {user?.user_type_id === UserTypeId.Student && (
                                                <Cell>Delete</Cell>)}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {courses?.map((course) => (
                                            <TableRow key={course.id}>
                                                <InnerCell>
                                                    {course.isEditing ? (
                                                        <CommonFormField
                                                            type="email"
                                                            value={course.grade}
                                                            onChange={(e) => handleCourseChange(course.id, 'grade', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{course.grade}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {course.isEditing ? (
                                                        <CommonFormField
                                                            type="email"
                                                            value={course.sem1}
                                                            onChange={(e) => handleCourseChange(course.id, 'sem1', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{course.sem1}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {course.isEditing ? (
                                                        <CommonFormField
                                                            type="email"
                                                            value={course.fullYearGrade1}
                                                            onChange={(e) => handleCourseChange(course.id, 'fullYearGrade1', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{course.fullYearGrade1}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {course.isEditing ? (
                                                        <CommonFormField
                                                            type="email"
                                                            value={course.sem2}
                                                            onChange={(e) => handleCourseChange(course.id, 'sem2', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{course.sem2}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {course.isEditing ? (
                                                        <CommonFormField
                                                            type="email"
                                                            value={course.fullYearGrade2}
                                                            onChange={(e) => handleCourseChange(course.id, 'fullYearGrade2', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{course.fullYearGrade2}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {course.isEditing ? (
                                                        <CommonFormField
                                                            type="number"
                                                            value={course.totalSem}
                                                            onChange={(e) => handleCourseChange(course.id, 'totalSem', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{course.totalSem}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {course.isEditing ? (
                                                        <CommonFormField
                                                            type="number"
                                                            value={course.counselor}
                                                            onChange={(e) => handleCourseChange(course.id, 'counselor', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{course.counselor}</Typography>
                                                    )}
                                                </InnerCell>
                                                {user?.user_type_id === UserTypeId.Student && (
                                                    <InnerCell>
                                                        {course.isEditing ? (
                                                            <>
                                                                <IconButton size="small" onClick={() => handleSaveCourse(course.id)}>
                                                                    <SaveIcon sx={{ color: theme.palette.secondary.main }} />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => handleCancelCourse(course.id)}>
                                                                    <Box sx={{ background: 'red', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0.3 }}>
                                                                        <Close sx={{ color: 'white', fontSize: 12 }} />
                                                                    </Box>
                                                                </IconButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <IconButton size="small" onClick={() => handleDeleteCourse(course.id)}>
                                                                    <img src="/Images/trash.png" style={{ width: '20px' }} />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => handleEditCourse(course.id)}>
                                                                    <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </InnerCell>)}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {user?.user_type_id === UserTypeId.Student && (
                                <Box mt={1}>
                                    <CommonButton
                                        label="+ Add a Course"
                                        handleClick={handleAddCourse}
                                        variant="outlined"
                                        size="small"
                                        marginBottom="0px"
                                        width="12%"
                                        height="30px"
                                        borderRadius={1}
                                        background="white"
                                        border={`1px solid ${theme.palette.background.secondary}`}
                                        color='black'
                                    />
                                </Box>)}
                        </Box>

                        {/* Extracurricular Activities Table */}
                        <Box sx={{ padding: '13px' }}>
                            <Typography fontWeight={600} mb={1}>
                                Extracurricular Activities
                            </Typography>
                            <TableContainer sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 1 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                            <Cell width="23%">Activity</Cell>
                                            <Cell width="23%">Start Date</Cell>
                                            <Cell width="23%">Location(s)</Cell>
                                            <Cell width="23%">No Volunteers</Cell>
                                            {user?.user_type_id === UserTypeId.Student && (
                                                <Cell>Delete</Cell>)}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activities?.map((activity) => (
                                            <TableRow key={activity.id}>
                                                <InnerCell>
                                                    {activity.isEditing ? (
                                                        <CommonFormField
                                                            type="email"
                                                            value={activity.activity}
                                                            onChange={(e) => handleActivityChange(activity.id, 'activity', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{activity.activity}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {activity.isEditing ? (
                                                        <CommonFormField
                                                            type="email"
                                                            value={activity.date}
                                                            onChange={(e) => handleActivityChange(activity.id, 'date', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{moment(activity.date)?.format('DD MMM, YYYY')}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {activity.isEditing ? (
                                                        <CommonFormField
                                                            type="email"
                                                            value={activity.location}
                                                            onChange={(e) => handleActivityChange(activity.id, 'location', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{activity.location}</Typography>
                                                    )}
                                                </InnerCell>
                                                <InnerCell>
                                                    {activity.isEditing ? (
                                                        <CommonFormField
                                                            type="number"
                                                            value={activity.volunteers}
                                                            onChange={(e) => handleActivityChange(activity.id, 'volunteers', e.target.value)}
                                                            placeholder=" "
                                                        />
                                                    ) : (
                                                        <Typography>{activity.volunteers}</Typography>
                                                    )}
                                                </InnerCell>
                                                {user?.user_type_id === UserTypeId.Student && (
                                                    <InnerCell>
                                                        {activity.isEditing ? (
                                                            <>
                                                                <IconButton size="small" onClick={() => handleSaveActivity(activity.id)}>
                                                                    <SaveIcon sx={{ color: theme.palette.secondary.main }} />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => handleCancelActivity(activity.id)}>
                                                                    <Box sx={{ background: 'red', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0.3 }}>
                                                                        <Close sx={{ color: 'white', fontSize: 12 }} />
                                                                    </Box>
                                                                </IconButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <IconButton size="small" onClick={() => handleDeleteActivity(activity.id)}>
                                                                    <img src="/Images/trash.png" style={{ width: '20px' }} />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => handleEditActivity(activity.id)}>
                                                                    <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </InnerCell>)}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {user?.user_type_id === UserTypeId.Student && (
                                <Box mt={1}>
                                    <CommonButton
                                        label="+ Add an Activity"
                                        handleClick={handleAddActivity}
                                        variant="outlined"
                                        size="small"
                                        width="12%"
                                        height="30px"
                                        borderRadius={1}
                                        marginBottom="0px"
                                        background="white"
                                        color='black'
                                        border={`1px solid ${theme.palette.background.secondary}`}
                                    />
                                </Box>)}
                        </Box>
                    </Fragment>
                }
                footerContent={
                    user?.user_type_id === UserTypeId.Student && (
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            width="100%"
                            paddingRight="5px"
                        >
                            <CommonButton
                                label="Submit"
                                handleClick={handleSubmit}
                                marginBottom="10px"
                                fontWeight={600}
                                width='50%'
                            />
                        </Box>)
                }
            />
        </Fragment>
    )
}

export default IntakeForm;